import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Plus, Check, Info, Activity, Brain } from 'lucide-react';
import { categories } from '../../data/habitCategories';

interface Goal {
  icon: React.ReactNode;
  name: string;
  target: number;
  completed: number;
  totalDays: number;
  unit?: string;
}

interface GoalCustomizerProps {
  onClose: () => void;
  currentGoals: Goal[];
}

export function GoalCustomizer({ onClose, currentGoals }: GoalCustomizerProps) {
  const [selectedTab, setSelectedTab] = useState<'activities' | 'behaviors'>('activities');
  const [selectedGoals, setSelectedGoals] = useState<string[]>(
    currentGoals.map(g => g.name)
  );

  const handleGoalToggle = (goalName: string) => {
    setSelectedGoals(prev =>
      prev.includes(goalName)
        ? prev.filter(g => g !== goalName)
        : [...prev, goalName]
    );
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-background rounded-xl border border-border max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="p-6 border-b border-border flex items-center justify-between">
          <h2 className="text-xl font-semibold">Customize Goals</h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 p-4">
          <button
            onClick={() => setSelectedTab('activities')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              selectedTab === 'activities'
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/70'
            }`}
          >
            <Activity className="w-4 h-4" />
            Activities
          </button>
          <button
            onClick={() => setSelectedTab('behaviors')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              selectedTab === 'behaviors'
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/70'
            }`}
          >
            <Brain className="w-4 h-4" />
            Behaviors
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-6">
            {categories.map((category) => (
              <div key={category.id} className="space-y-4">
                <h3 className="text-sm font-medium text-muted-foreground">
                  {category.name}
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {category.habits.map((habit) => {
                    const isSelected = selectedGoals.includes(habit.name);
                    return (
                      <button
                        key={habit.id}
                        onClick={() => handleGoalToggle(habit.name)}
                        className={`p-4 rounded-lg text-left relative group overflow-hidden ${
                          isSelected
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted hover:bg-muted/70'
                        }`}
                      >
                        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                        <div className="relative flex items-center justify-between">
                          <div>
                            <div className="font-medium">{habit.name}</div>
                            <div className="text-xs opacity-80">Target: {habit.defaultTarget} days/week</div>
                          </div>
                          {isSelected ? (
                            <Check className="w-4 h-4" />
                          ) : (
                            <Plus className="w-4 h-4" />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
            <Info className="w-4 h-4" />
            <span>Select up to 5 goals to track at once</span>
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 bg-muted hover:bg-muted/70 text-muted-foreground px-4 py-2 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onClose}
              className="flex-1 theme-button px-4 py-2 rounded-lg"
            >
              Save Changes
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}